<script setup>
import { computed, ref } from 'vue'
import { X, Check, Crown, Loader2 } from 'lucide-vue-next'
import { usePlanStore, TIER_ORDER } from '@/stores/planStore'
import { usePolar } from '@/composables/usePolar'
import { TIERS, priceFor, perPageCents, annualSavingsPct } from '@/data/pricing'
import RedirectOverlay from '@/components/billing/RedirectOverlay.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

const plan = usePlanStore()
const polar = usePolar()
const interval = ref('month') // 'month' | 'year' — deploy gate 4 toggle

// pricing.js is the single source — same data the public PricingView renders,
// so the two stay identical.
const cards = computed(() => TIERS.map((t) => ({
  ...t,
  display: priceFor(t, interval.value),
  centsPerPage: perPageCents(t, interval.value),
  savingsPct: interval.value === 'year' ? annualSavingsPct(t) : null,
  isCurrent: t.id === plan.tier,
})))
const currentTierIndex = computed(() => TIER_ORDER.indexOf(plan.tier))

function close() {
  emit('update:modelValue', false)
}

// Which card's button was clicked — drives its inline spinner while the
// full-screen RedirectOverlay covers the checkout handoff.
const redirectingTier = ref(null)

function selectTier(tierId) {
  if (plan.isMockMode) {
    plan.setTier(tierId, interval.value)
    close()
    return
  }
  if (tierId === 'free') return
  redirectingTier.value = tierId
  // Existing paid subscribers change plans in the Polar portal — a fresh
  // checkout would create a SECOND subscription alongside the current one.
  const nav = plan.isPaid
    ? polar.openPortal()
    : polar.redirectToCheckout(polar.productKey(tierId, interval.value))
  nav.catch(() => { redirectingTier.value = null })
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <!-- Teleported to body → must set its own text colors (it escapes the
           app wrapper that provides dark:text-slate-100). -->
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 text-slate-900 backdrop-blur-sm dark:text-slate-100" @click.self="close">
        <div class="card-surface relative max-h-[90vh] w-full max-w-5xl overflow-y-auto p-6 sm:p-8">
          <button type="button" class="absolute right-4 top-4 rounded-lg p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close" @click="close">
            <X class="h-5 w-5" />
          </button>

          <header class="text-center">
            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Crown class="h-6 w-6" />
            </div>
            <h2 class="mt-4 font-display text-2xl font-bold">Choose your plan</h2>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              You can change or cancel any time.
            </p>

            <div class="mt-4 inline-flex rounded-lg border border-slate-200 p-0.5 dark:border-slate-700" role="radiogroup" aria-label="Billing interval">
              <button
                type="button"
                role="radio"
                :aria-checked="interval === 'month'"
                class="rounded-md px-4 py-1.5 text-sm font-medium transition"
                :class="interval === 'month' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'"
                @click="interval = 'month'"
              >
                Monthly
              </button>
              <button
                type="button"
                role="radio"
                :aria-checked="interval === 'year'"
                class="flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition"
                :class="interval === 'year' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'"
                @click="interval = 'year'"
              >
                Annual
                <span class="pill text-[10px]" :class="interval === 'year' ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'">2 months free</span>
              </button>
            </div>
          </header>

          <div class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div
              v-for="(tier, index) in cards"
              :key="tier.id"
              class="card-surface relative flex flex-col p-5"
              :class="[
                tier.isCurrent ? 'ring-2 ring-primary' : '',
                tier.featured && !tier.isCurrent ? 'ring-1 ring-primary/40' : '',
              ]"
            >
              <span
                v-if="tier.featured && !tier.isCurrent"
                class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white"
              >Most popular</span>

              <div class="flex items-baseline justify-between">
                <h3 class="font-display text-lg font-semibold" :class="tier.featured ? 'text-primary' : ''">{{ tier.name }}</h3>
                <span v-if="tier.isCurrent" class="pill bg-primary/10 text-xs text-primary">Current</span>
              </div>

              <p class="mt-2 text-3xl font-bold">
                {{ tier.display.amount }}<span v-if="tier.display.per" class="text-base font-normal text-slate-500">{{ tier.display.per }}</span>
              </p>
              <p class="h-4 text-xs text-slate-500 dark:text-slate-400">
                {{ tier.display.note }}<span v-if="tier.savingsPct" class="ml-1 font-medium text-emerald-600 dark:text-emerald-400">· save {{ tier.savingsPct }}%</span>
              </p>

              <p class="mt-3 border-b border-slate-100 pb-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
                <strong class="text-slate-900 dark:text-white">{{ tier.pages.toLocaleString() }}</strong> pages/mo
                <span v-if="tier.centsPerPage" class="ml-1 text-xs text-slate-400">({{ tier.centsPerPage }}¢/page)</span>
              </p>

              <ul class="mt-4 flex-1 space-y-2 text-sm">
                <li v-for="feat in tier.features" :key="feat" class="flex items-start gap-2">
                  <Check class="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <span>{{ feat }}</span>
                </li>
              </ul>

              <button
                type="button"
                class="mt-5 flex w-full items-center justify-center gap-2"
                :class="tier.isCurrent ? 'btn-secondary' : (tier.featured ? 'btn-primary' : 'btn-secondary')"
                :disabled="tier.isCurrent || polar.isRedirecting.value"
                @click="selectTier(tier.id)"
              >
                <template v-if="redirectingTier === tier.id"><Loader2 class="h-4 w-4 animate-spin" /> Opening checkout…</template>
                <template v-else-if="tier.isCurrent">Current plan</template>
                <template v-else-if="index < currentTierIndex">Downgrade to {{ tier.name }}</template>
                <template v-else-if="tier.id === 'free'">Continue free</template>
                <template v-else>Upgrade to {{ tier.name }}</template>
              </button>
            </div>
          </div>

          <p class="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
            Need the occasional extra page? Every paid plan supports pay-as-you-go overage — you set the monthly budget in Settings.
          </p>

          <p v-if="plan.isMockMode" class="mt-4 rounded-lg bg-amber-50 p-3 text-center text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            Demo mode — selecting a tier here just updates the local plan. Polar checkout wires up after staging deploy.
          </p>
        </div>
      </div>
    </Transition>

    <RedirectOverlay
      :show="polar.isRedirecting.value"
      :label="plan.isPaid ? 'Opening your billing portal…' : 'Opening secure checkout…'"
    />
  </Teleport>
</template>
